import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { UpdateTenantProductPayload } from "@surgia/ui/dtos/request";
import Badge from "@surgia/ui/components/badge";
import VariantIcon from "@surgia/ui/icons/catalog/variantProduct.svg";
import { useCustomerView } from "../../hooks/useCustomerView";
import { useUpdateProduct } from "../../hooks/useUpdateProduct";
import ProductFormFields from "./ProductFormFields";
import ProductActions from "./ProductActions";
import { VariantCardProps, ProductFormData } from "./types";

/**
 * Memoized VariantCard component for displaying and managing product variants
 */
const VariantCard = React.memo<VariantCardProps>(
  ({
    variant,
    product,
    onToggleExpansion,
    expandedVariants,
    isProductDetailsPage,
    showOnlyCurrentlyAssignedVariants,
    tenantId,
    currencyCode, // Add this prop
  }) => {
    const isVariantExpanded = expandedVariants.has(variant.id.toString());

    // Use the useUpdateProduct hook
    const { updateProduct, isUpdating } = useUpdateProduct({
      parentProductId: product?.id?.toString() || "",
      tenantId: tenantId || "",
    });

    const isVariantSaving = isUpdating;

    // React Hook Form for variant
    const {
      register,
      formState: { isDirty, errors },
      reset,
      watch,
    } = useForm<ProductFormData>({
      defaultValues: {
        price: variant?.price ?? null,
        minQuantity: variant?.minQuantity ?? null,
        maxQuantity: variant?.maxQuantity ?? null,
        customerProductId: variant?.customerProductId || "",
      },
      mode: "onChange",
    });

    const currentFormValues = watch();

    // Custom hook for customer view management
    const {
      customerView: variantCustomerView,
      toggleCustomerView: toggleVariantCustomerView,
      isDisabled: isVariantCustomerViewDisabled,
      isLoading: isVariantCustomerViewLoading,
    } = useCustomerView({
      initialCustomerView: variant?.customer_view,
      productId: variant.id.toString(),
      isDisabled:
        variant?.price === null ||
        variant?.maxQuantity === null ||
        variant?.minQuantity === null,
      tenantId: tenantId || "",
      parentProductId: product?.id?.toString(),
    });

    // Check if parent product is visible for variant visibility logic
    if (showOnlyCurrentlyAssignedVariants && !variant?.isVisible) {
      return null;
    }

    // Handle variant customer view toggle with parent visibility check
    const handleVariantCustomerViewToggle = () => {
      if (product.customer_view === false) {
        toast.info(
          "To change variant visibility, the parent product must be visible first."
        );
        return;
      }
      toggleVariantCustomerView();
    };

    // Handle variant save operation - TODO: Implement variant save logic
    const handleVariantSave = async () => {
      const formData: ProductFormData = {
        price: currentFormValues.price || null,
        minQuantity: currentFormValues.minQuantity || null,
        maxQuantity: currentFormValues.maxQuantity || null,
        customerProductId: currentFormValues.customerProductId || "",
      };

      if (!formData.minQuantity || !formData.maxQuantity || !formData.price) {
        toast.error("Please fill in all required fields correctly.");
        return;
      }

      const tenantProductId = variant.tenantProductId || variant.id.toString();

      if (!tenantProductId) {
        toast.error("Tenant Product ID is missing. Cannot save variant.");
        return;
      }

      const payload: UpdateTenantProductPayload = {
        minimumQuantity: Number(formData.minQuantity),
        maximumQuantity: Number(formData.maxQuantity),
        tenantProductName: formData.customerProductName || "",
        tenantProductSku: formData.customerProductId || "",
        productPrices: [
          {
            price: Number(formData.price),
            currency: currencyCode!, // Use dynamic currency
          },
        ],
      };

      try {
        await updateProduct({
          tenantProductId: tenantProductId,
          payload,
        });

        // Form data is now managed by React Hook Form and fresh data comes from API
        reset(formData);
      } catch (error) {
        console.error("Error saving variant:", error);
      }
    };

    // Check form validity
    const isFormValid =
      currentFormValues.minQuantity !== null &&
      currentFormValues.maxQuantity !== null &&
      currentFormValues.minQuantity !== undefined &&
      currentFormValues.maxQuantity !== undefined &&
      Number(currentFormValues.maxQuantity) >=
        Number(currentFormValues.minQuantity);

    // Feature display and image are now part of the normalized variant data
    const featureDisplay = variant?.name || "";
    const variantImage = variant?.image;

    return (
      <div
        className="bg-gray-50 rounded-lg border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Variant Header */}
        <div
          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
          onClick={() => onToggleExpansion(variant.id)}
        >
          <div className="flex items-center gap-3">
            <img
              src={variantImage ?? VariantIcon}
              alt="variant"
              className="rounded-full w-[40px] h-[40px]"
            />
            <div>
              <h4 className="text-gray-900 font-medium text-sm">
                {product?.catalog_info?.name}
              </h4>
              <p className="text-xs text-gray-500">{featureDisplay}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Status Badge */}
            {!isProductDetailsPage && (
              <Badge
                variant={isVariantCustomerViewDisabled ? "default" : "active"}
                size="sm"
                className="!rounded-lg"
              >
                {isVariantCustomerViewDisabled ? "Draft" : "Completed"}
              </Badge>
            )}
            {isProductDetailsPage && (
              <Badge
                variant={!variantCustomerView ? "default" : "active"}
                size="sm"
                className="!rounded-lg"
              >
                {!variantCustomerView ? "Draft" : "Published"}
              </Badge>
            )}
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isVariantExpanded ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Variant Expanded Content */}
        {isVariantExpanded && (
          <div className="px-4 py-4 border-t border-gray-200 space-y-4">
            <ProductFormFields
              register={register}
              errors={errors}
              showCustomerProductName={false}
            />
            <ProductActions
              customerView={variantCustomerView}
              isCustomerViewDisabled={isVariantCustomerViewDisabled}
              isCustomerViewLoading={isVariantCustomerViewLoading}
              onToggleCustomerView={handleVariantCustomerViewToggle}
              onCancel={() => reset()}
              onSave={handleVariantSave}
              isSaving={isVariantSaving}
              isDirty={isDirty}
              hasErrors={Object.keys(errors).length > 0}
              isFormValid={isFormValid}
            />
          </div>
        )}
      </div>
    );
  }
);

export default VariantCard;
